// Isomorphic translation core: types, lookup, ICU-lite interpolation, plurals, rich text and
// Intl formatting. No Next.js / server imports — used by getT (server), useT (client), tests
// and scripts/i18n-check.ts.
import { Fragment, createElement, type ReactNode } from "react";
import { intlLocale, type UiLocale } from "./config";

// ── Types ──────────────────────────────────────────────────────────────────────

export const PLURAL_CATEGORIES = ["zero", "one", "two", "few", "many", "other"] as const;
export type PluralCategory = (typeof PLURAL_CATEGORIES)[number];
/**
 * A plural message is an object with an `other` key and only CLDR category keys. English
 * usually needs { one, other }; Arabic can use all six (zero/one/two/few/many/other).
 * `zero` is also honoured in languages whose rules never select it (en/fr) when count is 0.
 */
export type PluralForms = { other: string } & Partial<Record<Exclude<PluralCategory, "other">, string>>;

type IsPlural<T> = T extends { other: string } ? (Exclude<keyof T, PluralCategory> extends never ? true : false) : false;

/** The shape every locale must satisfy, derived from the English source dictionary. */
export type Shape<T> = T extends string ? string : IsPlural<T> extends true ? PluralForms : { [K in keyof T]: Shape<T[K]> };

/** Dotted keys of all leaves (strings and plural objects) of a dictionary shape. */
export type Leaves<T, P extends string = ""> = T extends string
  ? P
  : IsPlural<T> extends true
    ? P
    : { [K in keyof T & string]: Leaves<T[K], P extends "" ? K : `${P}.${K}`> }[keyof T & string];

export type Values = Record<string, string | number | null | undefined>;
/** Rich values: plain values, or render functions for <tag>…</tag> markup in the message. */
export type RichValues = Record<string, string | number | null | undefined | ReactNode | ((chunks: ReactNode) => ReactNode)>;

export type MessageTree = { [key: string]: string | MessageTree };

export interface Translator<K extends string = string> {
  (key: K, values?: Values): string;
  /** Messages with <tag>chunks</tag> markup, e.g. "Already a member? <link>Sign in</link>". */
  rich(key: K, values?: RichValues): ReactNode;
  /** True when the key exists (useful for optional / data-driven keys). */
  has(key: string): boolean;
  /** Translate a data-driven key that isn't statically typed; returns `fallback` when missing. */
  dynamic(key: string, values?: Values, fallback?: string): string;
  locale: UiLocale;
}

// ── Lookup ─────────────────────────────────────────────────────────────────────

export function lookup(tree: unknown, path: string): unknown {
  let cur: unknown = tree;
  for (const part of path.split(".")) {
    if (!cur || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
}

export function isPluralForms(v: unknown): v is PluralForms {
  if (!v || typeof v !== "object") return false;
  const keys = Object.keys(v);
  return keys.includes("other") && keys.every((k) => (PLURAL_CATEGORIES as readonly string[]).includes(k)) && keys.every((k) => typeof (v as Record<string, unknown>)[k] === "string");
}

const pluralRulesCache = new Map<string, Intl.PluralRules>();
export function pluralCategory(locale: UiLocale, count: number): PluralCategory {
  const tag = intlLocale(locale);
  let pr = pluralRulesCache.get(tag);
  if (!pr) pluralRulesCache.set(tag, (pr = new Intl.PluralRules(tag)));
  return pr.select(count) as PluralCategory;
}

export function selectPlural(locale: UiLocale, forms: PluralForms, count: number): string {
  if (count === 0 && forms.zero !== undefined) return forms.zero;
  const cat = pluralCategory(locale, count);
  return forms[cat] ?? forms.other;
}

/** Replace {name} placeholders. Numbers are locale-formatted; unknown placeholders are kept verbatim. */
export function interpolate(locale: UiLocale, message: string, values?: Values): string {
  if (!values) return message;
  return message.replace(/\{(\w+)\}/g, (m, name: string) => {
    const v = values[name];
    if (v === undefined || v === null) return m;
    return typeof v === "number" ? formatNumber(locale, v) : String(v);
  });
}

function resolve(locale: UiLocale, tree: unknown, key: string, values?: Values | RichValues): string | undefined {
  const raw = lookup(tree, key);
  if (typeof raw === "string") return raw;
  if (isPluralForms(raw)) {
    const count = Number((values as Values | undefined)?.count ?? 0);
    return selectPlural(locale, raw, Number.isFinite(count) ? count : 0);
  }
  return undefined;
}

const warned = new Set<string>();
function warnMissing(key: string) {
  if (process.env.NODE_ENV === "production" || warned.has(key)) return;
  warned.add(key);
  console.warn(`[ui-i18n] missing message "${key}"`);
}

/** Render "<b>bold</b> and {name}" with tag functions. Tags are not nested. */
export function renderRich(locale: UiLocale, message: string, values?: RichValues): ReactNode {
  const plain: Values = {};
  for (const [k, v] of Object.entries(values ?? {})) if (typeof v === "string" || typeof v === "number") plain[k] = v;
  const parts: ReactNode[] = [];
  const re = /<(\w+)>(.*?)<\/\1>|\{(\w+)\}/gs;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(message))) {
    if (m.index > last) parts.push(message.slice(last, m.index));
    if (m[1] !== undefined) {
      const fn = values?.[m[1]];
      const inner = interpolate(locale, m[2] ?? "", plain);
      parts.push(typeof fn === "function" ? createElement(Fragment, { key: `r${i++}` }, (fn as (c: ReactNode) => ReactNode)(inner)) : inner);
    } else {
      const v = values?.[m[3]!];
      if (v === undefined || v === null) parts.push(m[0]);
      else if (typeof v === "number") parts.push(formatNumber(locale, v));
      else if (typeof v === "function") parts.push(m[0]);
      else parts.push(typeof v === "string" ? v : createElement(Fragment, { key: `v${i++}` }, v as ReactNode));
    }
    last = re.lastIndex;
  }
  if (last < message.length) parts.push(message.slice(last));
  return parts.length === 1 && typeof parts[0] === "string" ? parts[0] : createElement(Fragment, null, ...parts);
}

/**
 * Build a translator over `messages` (a full dictionary or the namespaces passed to the
 * client). With `namespace`, keys are relative to it ("login.title" instead of "auth.login.title").
 * A missing key renders the key itself (and warns once in development) — never crashes.
 */
export function createTranslator<K extends string = string>(locale: UiLocale, messages: unknown, namespace?: string): Translator<K> {
  const full = (key: string) => (namespace ? `${namespace}.${key}` : key);
  const t = ((key: string, values?: Values) => {
    const msg = resolve(locale, messages, full(key), values);
    if (msg === undefined) {
      warnMissing(full(key));
      return full(key);
    }
    return interpolate(locale, msg, values);
  }) as unknown as Translator<K>;
  t.rich = (key, values) => {
    const msg = resolve(locale, messages, full(key), values);
    if (msg === undefined) {
      warnMissing(full(key));
      return full(key);
    }
    return renderRich(locale, msg, values);
  };
  t.has = (key) => resolve(locale, messages, full(key), { count: 1 }) !== undefined;
  t.dynamic = (key, values, fallback) => {
    const msg = resolve(locale, messages, full(key), values);
    return msg === undefined ? (fallback ?? full(key)) : interpolate(locale, msg, values);
  };
  t.locale = locale;
  return t;
}

// ── Formatting (deterministic: same output on server and client) ───────────────
// RULE: never format a Date during render without an explicit timeZone. These helpers
// default to "UTC" so SSR and hydration produce identical text. For the viewer's local
// time zone, or for "x minutes ago" (depends on now), render on the client in an effect
// (see <LocalDateTime>/<TimeAgo> in src/components/ui/time.tsx).

const nfCache = new Map<string, Intl.NumberFormat>();
export function formatNumber(locale: UiLocale, n: number, opts?: Intl.NumberFormatOptions): string {
  const key = `${locale}|${JSON.stringify(opts ?? {})}`;
  let nf = nfCache.get(key);
  if (!nf) nfCache.set(key, (nf = new Intl.NumberFormat(intlLocale(locale), opts)));
  return nf.format(n);
}

export function formatCurrency(locale: UiLocale, amount: number, currency: string, opts?: Intl.NumberFormatOptions): string {
  return formatNumber(locale, amount, { style: "currency", currency: currency.toUpperCase(), ...opts });
}

export type DateStyle = "date" | "datetime" | "time" | "monthYear" | "short";
const DATE_PRESETS: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  date: { year: "numeric", month: "short", day: "numeric" },
  datetime: { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" },
  time: { hour: "numeric", minute: "2-digit" },
  monthYear: { year: "numeric", month: "long" },
  short: { month: "short", day: "numeric" },
};

export function formatDate(locale: UiLocale, date: Date | string | number, style: DateStyle | Intl.DateTimeFormatOptions = "date", timeZone = "UTC"): string {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "—";
  const opts = typeof style === "string" ? DATE_PRESETS[style] : style;
  return new Intl.DateTimeFormat(intlLocale(locale), { ...opts, timeZone: opts.timeZone ?? timeZone }).format(d);
}

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 365 * 86400],
  ["month", 30 * 86400],
  ["week", 7 * 86400],
  ["day", 86400],
  ["hour", 3600],
  ["minute", 60],
  ["second", 1],
];

/** "3 hours ago" / "il y a 3 heures" / "قبل 3 ساعات". `now` is explicit so the result is pure. */
export function formatRelative(locale: UiLocale, date: Date | string | number, now: Date | number): string {
  const d = date instanceof Date ? date : new Date(date);
  const diff = Math.round((d.getTime() - (now instanceof Date ? now.getTime() : now)) / 1000);
  const abs = Math.abs(diff);
  const rtf = new Intl.RelativeTimeFormat(intlLocale(locale), { numeric: "auto" });
  for (const [unit, secs] of UNITS) {
    if (abs >= secs || unit === "second") return rtf.format(Math.trunc(diff / secs), unit);
  }
  return rtf.format(0, "second");
}

export function formatList(locale: UiLocale, items: string[], type: "conjunction" | "disjunction" = "conjunction"): string {
  return new Intl.ListFormat(intlLocale(locale), { style: "long", type }).format(items);
}

export type Formatters = {
  locale: UiLocale;
  number: (n: number, opts?: Intl.NumberFormatOptions) => string;
  currency: (amount: number, currency: string, opts?: Intl.NumberFormatOptions) => string;
  date: (d: Date | string | number, style?: DateStyle | Intl.DateTimeFormatOptions, timeZone?: string) => string;
  relative: (d: Date | string | number, now: Date | number) => string;
  list: (items: string[], type?: "conjunction" | "disjunction") => string;
};

export function createFormatters(locale: UiLocale): Formatters {
  return {
    locale,
    number: (n, o) => formatNumber(locale, n, o),
    currency: (a, c, o) => formatCurrency(locale, a, c, o),
    date: (d, s, tz) => formatDate(locale, d, s, tz),
    relative: (d, now) => formatRelative(locale, d, now),
    list: (items, type) => formatList(locale, items, type),
  };
}
